import React from 'react';
import useCanvas from 'components/canvas/hooks/use-canvas';
import { useTranslation } from 'i18n';
import { useSoftDeleteSurfaceNodesMutation } from 'graphql/hooks/use-soft-delete-surface-nodes-mutation';
import { useCanvasClipboardOperations } from 'state/canvas-clipboard';
import { useSelectedAreaOperations } from 'state/canvas/selected-area';
import { isNull } from 'lodash';
import { notEmpty } from 'utils';
import { toast } from 'services/toast';
import useSelectedNodes, {
  SelectedNodes,
} from 'components/canvas/components/bulk-select-provider/components/selection-rectangle/hooks/use-selected-nodes';
import { AnyObject, Dimension, Position } from 'types';
import useIsCanvasDraggable from 'components/canvas/hooks/use-is-canvas-draggable';
import useIsCanvasSelectable from 'components/canvas/hooks/use-is-canvas-selectable';
import Konva from 'konva';
import { SELECTION_GROUP_ID } from 'components/canvas/components/bulk-select-provider/components/selection-rectangle/index';
import useSelectedNodesWithPermissionToMakeAnAction from 'components/canvas/components/bulk-select-provider/components/selection-rectangle/hooks/use-selected-nodes-with-permission-to-make-an-action';

interface UseSelectionRectangleOperations {
  snappedDimensions: (Position & Dimension) | AnyObject;
  cutNodes: Function;
  copyNodes: Function;
  deleteNodes: Function;
  selectedNodes: SelectedNodes;
  selectionRectangleInstance: Konva.Group | null;
}

export default function useSelectionRectangleOperations(
  surface: string,
): UseSelectionRectangleOperations {
  const { deleteConfirmModalRef, findShapeById } = useCanvas();

  const { t } = useTranslation();

  const { clearSelectedArea } = useSelectedAreaOperations();

  const softDeleteNodes = useSoftDeleteSurfaceNodesMutation(surface);

  const [__, setCanvasDraggable] = useIsCanvasDraggable();

  const [_, setIsCanvasSelectable] = useIsCanvasSelectable();

  const selectionRectangleInstance = findShapeById(SELECTION_GROUP_ID);

  const {
    removeNodesFromClipboard,
    addNodesForCutToClipboard,
    addNodesForCopyToClipboard,
  } = useCanvasClipboardOperations();

  const selectedNodes = useSelectedNodes(surface);

  const {
    selectedNodesAllowedToDelete,
    selectedNodesAllowedToCut,
    selectedNodesAllowedToCopy,
  } = useSelectedNodesWithPermissionToMakeAnAction(surface);

  const snappedDimensions = calculateSnappedContainerDimensions(selectedNodes);

  React.useEffect(() => {
    if (isNull(snappedDimensions)) {
      clearSelectedArea();
    }
  }, [snappedDimensions]);

  function toggleIsCanvasDraggable() {
    setIsCanvasSelectable(false);
    setCanvasDraggable(true);
  }

  async function deleteNodes() {
    clearSelectedArea();
    toggleIsCanvasDraggable();
    deleteConfirmModalRef.current?.setIsLoading(true);
    deleteConfirmModalRef.current?.show();

    const nodesWithReferences: string[] = [];

    try {
      /*const response = await surfaceNodesWithReferences(`
              { input: { filter: { ids: selectedNodesAllowedToDelete.map(({ id }) => id) } } },
              { fetchPolicy: 'network-only' },
            );

            if (response.isFailure) {
              deleteConfirmModalRef.current?.hide();
              deleteConfirmModalRef.current?.setIsLoading(false);
              toast.error(response.getErrorMessage());
              return;
            }

            const data = response.getData() || [];
            */
      deleteConfirmModalRef.current?.setIsLoading(false);

      const data: any = [];

      for (const entry of data) {
        if (entry.references?.length > 0) {
          nodesWithReferences.push(entry?.name);
        }
      }
    } catch (error) {
      deleteConfirmModalRef.current?.hide();

      deleteConfirmModalRef.current?.setIsLoading(false);

      toast.error(t('Something went wrong, could not complete the operation'));

      return;
    }

    function onConfirmHandler() {
      softDeleteNodes(selectedNodesAllowedToDelete);

      removeNodesFromClipboard(selectedNodes as any);
    }

    if (deleteConfirmModalRef.current) {
      deleteConfirmModalRef?.current.setData({
        name: selectedNodesAllowedToDelete.map((node) => node.name).join(', '),
        additionalText:
          nodesWithReferences.length > 0
            ? t(
                'Node {{names}} contain references that will be deleted. Do you want to proceed?',
                {
                  replace: {
                    names: nodesWithReferences.join(', '),
                  },
                },
              )
            : '',
        onConfirmHandler,
      });

      deleteConfirmModalRef.current?.setIsLoading(false);
    }
  }

  function cutNodes() {
    clearSelectedArea();
    toast.success(t(`Successfully added nodes to clipboard.`));

    addNodesForCutToClipboard(selectedNodesAllowedToCut as any);
    toggleIsCanvasDraggable();
  }

  function copyNodes() {
    clearSelectedArea();
    toast.success(t(`Successfully added nodes to clipboard.`));
    addNodesForCopyToClipboard(selectedNodesAllowedToCopy as any);
    toggleIsCanvasDraggable();
  }

  return {
    snappedDimensions: snappedDimensions || {},
    cutNodes,
    copyNodes,
    deleteNodes,
    selectedNodes,
    selectionRectangleInstance,
  };
}

export function calculateSnappedContainerDimensions(
  nodes: SelectedNodes,
): (Position & Dimension) | null {
  if (notEmpty(nodes)) {
    const points = nodes.map((node) => node.boundingBox);

    const x = points.sort((a, b) => (a[0][0] > b[0][0] ? 1 : -1))[0][0][0];
    const y = points.sort((a, b) => (a[0][1] > b[0][1] ? 1 : -1))[0][0][1];

    const width =
      points.sort((a, b) => (a[1][0] < b[1][0] ? 1 : -1))[0][1][0] - x;

    const height =
      points.sort((a, b) => (a[2][1] < b[2][1] ? 1 : -1))[0][2][1] - y;

    return {
      x,
      y,
      width,
      height,
    };
  }

  return null;
}
